import { Pressable, Text, View } from 'react-native';
import type { Exercise } from '@heraion/shared';
import { Icon, useDisclosure } from '../index';

import { useExerciseName } from './exerciseName';
import { useTheme } from './theme';
import type { WeightUnit } from './state/loggerDraft';
import type { DraftEntry } from './state/loggerDraftTypes';
import type { makeStyles } from './ExerciseSwapPicker.styles';
import { MachineTray } from './_MachineTray';
import { useMachineChoice } from './_useMachineChoice';
import type { ComponentType } from 'react';

// EQ-1 — one row of the swap picker, split out of the screen when the machine
// tray landed (the screen was already at the 350-line soft warning, and this
// is the same move that produced ExercisePickerRow for the shared picker).
//
// The row answers the three-case rule of equipment-model.md §8.3 through the
// SAME resolveInstanceChoice the add and replace paths use, so no surface can
// answer WHETHER TO INTERRUPT differently:
//
//   none / single → one tap, exactly like before; the instance (if any) is
//                   applied silently downstream by useReplaceExercise
//   choose        → the row opens in place instead of picking
//
// ⛔ The two callers deliberately pass DIFFERENT options to that one function,
// and this asymmetry is EQ-1-DEFAULT-WINS, not a bug to unify away. Here the
// question is "do I interrupt?", which the Default never changes, so the flag
// stays off. Downstream the question is "what do I attach without asking?",
// where a Default that carries history must win — so `single` can resolve to
// nothing there while this row still treats it as a one-tap case. The flag
// only ever demotes `single` to `none` and never touches `choose`, which is
// why the interrupt decision is provably identical either way.
//
// The glyph + count are visible BEFORE the tap precisely so an opening row is
// never a surprise (§8.3): a row that will expand says so.
type Props = {
  exercise: Exercise;
  /** Why this exercise was suggested; empty in the "All" section. */
  reasonChips: string[];
  /** `instance` is undefined when nobody asked — never a guess. */
  onPick: (next: Exercise, instance?: string | null) => void;
  /** EQ-1 slice D — the session's place. Ranks the tray, never trims it. */
  orderByLocation: string | null;
  /** The open session's entries — the live load signature. */
  liveEntries: readonly DraftEntry[];
  unit: WeightUnit;
  theme: ReturnType<typeof useTheme>;
  styles: ReturnType<typeof makeStyles>;
  /** Host-injected pain-risk dot (see bodymap/PainDot.tsx — each host
   *  supplies its own painMap source). Wired on both platforms since
   *  2026-08-17, same slot the shared ExercisePicker's own PainDot uses. */
  PainDot?: ComponentType<{ exercise: Exercise }>;
  usePainRiskLabel?: (exercise: Exercise) => string | null;
  /**
   * EQ-1-GYM-COLDSTART R6 — machines declared absent here, for THIS row's
   * exercise. ⚠️ DATA, not a control: this row documents above that it was
   * deliberately kept light, and R6 adds nothing to tap. It narrows what the
   * tray this row already opens is allowed to offer, and it is threaded here
   * rather than left out so the two tray callsites cannot answer the same
   * question differently — the drift `_useMachineChoice` exists to prevent.
   */
  absentHere?: readonly string[];
  /** EQ-1-PIN-DEFAULT — what the athlete elected for THIS candidate's exercise
   *  here. Read-only on this band: the ★ control lives on the current-exercise
   *  line (no per-row control on candidates — input fatigue), but an election
   *  already made must be HONOURED here too, or the tray keeps interrupting on
   *  the very path the athlete was trying to silence. */
  pinned?: string | null;
};

export function ExerciseSwapPickerRow({
  exercise,
  reasonChips,
  onPick,
  orderByLocation,
  liveEntries,
  unit,
  theme,
  styles,
  PainDot,
  usePainRiskLabel,
  absentHere,
  pinned,
}: Props) {
  const getExerciseName = useExerciseName();
  const disclosure = useDisclosure();
  const { known, fallback, asks, optionCount, countLabel, takenValues } =
    useMachineChoice(exercise.slug, {
      orderByLocation,
      liveEntries,
      ...(absentHere ? { absentHere } : {}),
      ...(pinned !== undefined ? { pinned } : {}),
    });
  // Host-injected hook, stable per mount: mobile always passes it, web never
  // does, and a given tree never switches host mid-life — so the hook order
  // cannot change between renders. Same exception class as the other optional
  // host slots in this file (hostDeps.ts).
  // ⚠️ The directive must be the LAST comment line before the code: an
  // `eslint-disable-next-line` suppresses the line that literally follows it,
  // so with the explanation underneath it landed on a comment and suppressed
  // nothing. Nobody noticed because packages/ui had no linter (CN-UI-LINT).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const painLabel = usePainRiskLabel ? usePainRiskLabel(exercise) : null;

  // Composed from every piece the eye gets, because a label on this Pressable
  // collapses the subtree: set it to the name alone and the pain warning, the
  // muscle and the reasons stop being announced at all. The machine count is
  // appended, not substituted.
  const a11yLabel = [
    getExerciseName(exercise.name),
    painLabel,
    exercise.primaryMuscle,
    ...reasonChips,
    asks ? countLabel : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <View>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={a11yLabel}
        {...(asks ? disclosure.a11yProps : {})}
        onPress={() => (asks ? disclosure.toggle() : onPick(exercise))}
        style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
      >
        <View style={styles.rowNameLine}>
          {PainDot ? <PainDot exercise={exercise} /> : null}
          <Text style={styles.rowName} numberOfLines={1}>
            {getExerciseName(exercise.name)}
          </Text>
          {asks ? (
            // Never colour-only: the glyph carries a written count, and the
            // chevron states the fold rather than implying it. Inlined again
            // once the current-exercise line moved to a labelled Chip and this
            // dropped back to a single callsite.
            <View style={styles.machineBadge}>
              <Icon name="tag" size={12} color={theme.palette.ink3} />
              <Text style={styles.machineBadgeText}>{optionCount}</Text>
              <Icon
                name={disclosure.expanded ? 'chevron-up' : 'chevron-down'}
                size={12}
                color={theme.palette.ink3}
              />
            </View>
          ) : null}
        </View>
        {exercise.primaryMuscle ? (
          <Text style={styles.rowSub} numberOfLines={1}>
            {exercise.primaryMuscle}
          </Text>
        ) : null}
        {reasonChips.length > 0 ? (
          <View style={styles.chipsRow}>
            {reasonChips.map((c, i) => (
              <View key={i} style={styles.reasonChip}>
                <Text style={styles.reasonChipText}>{c}</Text>
              </View>
            ))}
          </View>
        ) : null}
      </Pressable>
      {asks && disclosure.expanded ? (
        <MachineTray
          known={known}
          fallback={fallback}
          takenValues={takenValues}
          unit={unit}
          theme={theme}
          onChoose={(value) => onPick(exercise, value)}
          // ⭐ This also gives back what removing the "no machine" option cost
          // on 2026-08-13, and gives it back better: on a row with two or more
          // machines you can swap to the exercise on the unnamed default —
          // which is an answer, where the old option was a refusal.
          onClear={() => onPick(exercise, null)}
        />
      ) : null}
    </View>
  );
}
